"use client"

import React, { useEffect, useMemo, useState } from "react"
import { format } from "date-fns"
import { ChevronDown, X } from "lucide-react"
import { Control, FormProvider, useForm, useWatch } from "react-hook-form"
import FormMultiSelectInput from "@/shared/components/form-inputs/form-multi-select-input"
import FormTextInputWrapper from "@/shared/components/form-inputs/form-input-wrapper"
import { FormField } from "@/shared/components/ui/form"
import { Button } from "@/shared/components/ui/button"
import { Calendar } from "@/shared/components/ui/calendar"
import { DialogClose } from "@/shared/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { cn } from "@/shared/lib/utils"
import { workScopeStatusOptions } from "@/features/homeowners/constants/options"
import { useClaimedByOptions } from "@/features/homeowners/hooks/use-claimed-by-options"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import {
  PRIORITY_FILTER_KEYS,
  parseCommaList,
  applyPriorityFilterSearchParams,
  clearPriorityFilterParams,
} from "../lib/priority-filters"

const FILTER_PLACEHOLDER_CLASS = "text-sm text-muted-foreground/55"

type PriorityFilterFormValues = {
  pmAddedFrom?: string
  pmAddedTo?: string
  pmClaimedBy?: string[]
  pmWorkscopeStatus?: string[]
}

function parseYmd(value: string | null | undefined): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const [y, m, d] = value.split("-").map(Number)
  return new Date(y, m - 1, d)
}

function formatYmd(date: Date): string {
  return format(date, "yyyy-MM-dd")
}

function formatDisplayDate(value: string | null | undefined): string | null {
  const date = parseYmd(value)
  if (!date) return null
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
}

function DateEnteredRangeInput({
  control,
  setValue,
}: {
  control: Control<PriorityFilterFormValues>
  setValue: (name: "pmAddedFrom" | "pmAddedTo", value: string | undefined) => void
}) {
  const addedFrom = useWatch({ control, name: "pmAddedFrom" })
  const addedTo = useWatch({ control, name: "pmAddedTo" })
  const fromDate = parseYmd(addedFrom)
  const toDate = parseYmd(addedTo)
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<{ from?: Date; to?: Date } | undefined>(
    () => (fromDate || toDate ? { from: fromDate, to: toDate } : undefined)
  )

  useEffect(() => {
    const from = parseYmd(addedFrom)
    const to = parseYmd(addedTo)
    setRange(from || to ? { from, to } : undefined)
  }, [addedFrom, addedTo])

  const hasValue = !!addedFrom || !!addedTo
  const fromLabel = formatDisplayDate(addedFrom)
  const toLabel = formatDisplayDate(addedTo)
  const buttonLabel = !hasValue
    ? "Select dates..."
    : fromLabel && toLabel && fromLabel !== toLabel
      ? `${fromLabel} – ${toLabel}`
      : fromLabel || toLabel || "Select dates..."

  return (
    <FormField
      control={control}
      name="pmAddedFrom"
      render={() => (
        <FormTextInputWrapper
          inputGroupProps={{
            wrapperClassName: "w-full min-w-0",
            label: "Date Entered",
          }}
        >
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                role="combobox"
                aria-expanded={open}
                showLoader={false}
                className={cn(
                  "w-full justify-between h-9 border border-input bg-transparent px-3 py-2 text-sm shadow-sm hover:bg-accent/50 hover:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring rounded-full font-normal",
                  !hasValue && FILTER_PLACEHOLDER_CLASS
                )}
              >
                <span className="truncate text-left flex-1">{buttonLabel}</span>
                <span className="ml-2 flex shrink-0 items-center">
                  {hasValue && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Clear date entered"
                      className="flex h-4 w-4 items-center justify-center"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setValue("pmAddedFrom", undefined)
                        setValue("pmAddedTo", undefined)
                        setRange(undefined)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          e.stopPropagation()
                          setValue("pmAddedFrom", undefined)
                          setValue("pmAddedTo", undefined)
                          setRange(undefined)
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </span>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-2 z-[200]"
              align="start"
              data-avoid-dialog-close
            >
              <Calendar
                mode="range"
                selected={range === undefined ? undefined : { from: range.from, to: range.to }}
                onSelect={(next) => {
                  setRange(next)
                  setValue("pmAddedFrom", next?.from ? formatYmd(next.from) : undefined)
                  const end = next?.to ?? next?.from
                  setValue("pmAddedTo", end ? formatYmd(end) : undefined)
                }}
                defaultMonth={range?.from || range?.to || new Date()}
                numberOfMonths={2}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </FormTextInputWrapper>
      )}
    />
  )
}

function applyPriorityFilterParams(
  current: URLSearchParams,
  updates: {
    pmAddedFrom?: string
    pmAddedTo?: string
    pmClaimedBy?: string
    pmWorkscopeStatus?: string
  }
) {
  return applyPriorityFilterSearchParams(current, {
    addedFrom: updates.pmAddedFrom,
    addedTo: updates.pmAddedTo,
    claimedBy: updates.pmClaimedBy,
    workscopeStatus: updates.pmWorkscopeStatus,
  })
}

type PriorityFilterFormProps = {
  onSuccessCallback?: () => void
}

export default function PriorityFilterForm({ onSuccessCallback }: PriorityFilterFormProps) {
  const { options: claimedByOptions } = useClaimedByOptions()
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const defaultValues = useMemo<PriorityFilterFormValues>(() => ({
    pmAddedFrom: searchParams.get(PRIORITY_FILTER_KEYS.addedFrom) || undefined,
    pmAddedTo: searchParams.get(PRIORITY_FILTER_KEYS.addedTo) || undefined,
    pmClaimedBy: parseCommaList(searchParams.get(PRIORITY_FILTER_KEYS.claimedBy)),
    pmWorkscopeStatus: parseCommaList(searchParams.get(PRIORITY_FILTER_KEYS.workscopeStatus)),
  }), [searchParams])

  const form = useForm<PriorityFilterFormValues>({
    defaultValues,
  })
  const { control, setValue } = form

  const pushParams = (params: URLSearchParams) => {
    const query = params.toString()
    router.push(query ? `${pathname}?${query}` : pathname)
  }

  const onSubmit = (data: PriorityFilterFormValues) => {
    const params = applyPriorityFilterParams(searchParams, {
      pmAddedFrom: data.pmAddedFrom,
      pmAddedTo: data.pmAddedTo,
      pmClaimedBy: data.pmClaimedBy?.length ? data.pmClaimedBy.join(",") : undefined,
      pmWorkscopeStatus: data.pmWorkscopeStatus?.length ? data.pmWorkscopeStatus.join(",") : undefined,
    })
    onSuccessCallback?.()
    pushParams(params)
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start"
      >
        <DateEnteredRangeInput
          control={control}
          setValue={(name, value) => setValue(name, value, { shouldDirty: true })}
        />
        <FormMultiSelectInput
          name="pmClaimedBy"
          options={claimedByOptions}
          placeholder="Select organizations..."
          triggerClassName="rounded-full"
          placeholderClassName={FILTER_PLACEHOLDER_CLASS}
          showSelectAll
          inputGroupProps={{
            wrapperClassName: "w-full min-w-0",
            label: "Claimed By",
          }}
          control={control}
        />
        <FormMultiSelectInput
          name="pmWorkscopeStatus"
          options={workScopeStatusOptions}
          placeholder="Select status..."
          triggerClassName="rounded-full"
          placeholderClassName={FILTER_PLACEHOLDER_CLASS}
          showSelectAll
          inputGroupProps={{
            wrapperClassName: "w-full min-w-0",
            label: "Status of Workscope",
          }}
          control={control}
        />
        <div className="grid grid-cols-[auto_1fr] gap-3 col-span-full pt-3">
          <DialogClose asChild>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" className="w-full">
            Apply
          </Button>
        </div>
        <div className="col-span-full pt-2">
          <Button
            variant="ghost"
            type="button"
            className="w-full text-sm"
            onClick={() => {
              form.reset({
                pmAddedFrom: undefined,
                pmAddedTo: undefined,
                pmClaimedBy: [],
                pmWorkscopeStatus: [],
              })
              pushParams(clearPriorityFilterParams(searchParams))
              onSuccessCallback?.()
            }}
          >
            Clear all filters
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
